<template>
    <div>
        <v-data-table
            dense
            show-select
            single-select
            @item-selected="setSelectedFromCheckbox"
            @click:row="setSelected"
            @dblclick:row="openSalesOrderDisplayDialog"
            @contextmenu:row="openContextMenu"
            :options.sync="options"
            :loading="loading"
            :items="salesOrders"
            :disable-pagination="!pagination"
            :hide-default-footer="!pagination"
            :server-items-length="length"
            :footer-props="{ 'items-per-page-options': [5,10,15] }"
            :headers="headers"
            v-model="selected">
            <template v-slot:[`item.validity_date`]="{ item }">
                <v-badge dot :color="getColor(item.validity_date)" inline left></v-badge>{{ item.validity_date }}
            </template>
            <template v-slot:[`item.total`]="{ item }">
                {{ formatMoney(item.total, currency, decimal) }}
            </template>
            <template v-slot:[`item.delivered`]="{ item }">
                {{ formatNumber(item.delivered, decimal) }}
            </template>
            <template v-slot:[`item.remaining`]="{ item }">
                {{ formatNumber(item.remaining, decimal) }}
            </template>
            <template v-slot:[`item.extended`]="{ item }">
                {{ formatNumber(item.extended, decimal) }}
            </template>
            <template v-slot:[`item.state`]="{ item }">
                <v-badge dot :color="item.state == 'CLOSED' ? 'error' : 'success' " inline left></v-badge>{{ item.state }}
            </template>
        </v-data-table>
        <ContextMenu
            v-if="contextMenu.display"
            :position="contextMenu.position"
            :items="contextMenuItems"
            @contextmenu:click="contextMenuClicked"/>
        <SalesOrderDisplayDialog
            ref="salesOrderDisplayDialog"/>
        <SalesOrderDialog
            ref="salesOrderDialog"
            @submit="$emit('updated', options)"/>
        <DeleteSingleDialog
            ref="deleteSingleDialog"
            @submit="onDeleteDialogSubmit"/>
        <AlertDialog
            ref="alertDialog"/>
    </div>
</template>

<script>
import {mapActions, mapState} from 'vuex';
import {formatMoney, formatNumber} from 'accounting';
import moment from 'moment';
import ContextMenu from '../common/ContextMenu';
import SalesOrderDisplayDialog from '../Sales/SalesOrderDisplayDialog';
import SalesOrderDialog from '../Sales/SalesOrderDialog';
import DeleteSingleDialog from '../common/DeleteSingleDialog'; 
import AlertDialog from '../common/AlertDialog';

export default {
    name: "SalesOrdersDataTable",
    components: {
        ContextMenu,
        SalesOrderDisplayDialog,
        SalesOrderDialog,
        DeleteSingleDialog,
        AlertDialog
    },
    props: {
        loading: {
            type: Boolean,
            required: true
        },
        salesOrders: {
            type: Array,
            required: true
        },
        length: {
            type: Number,
            required: true
        },
        pagination: {
            type: Boolean,
            required: true
        }
    },
    watch: {
        options: {
            handler() {
                this.$emit("updated", this.options)
            }
        }
    },
    data() {
        return {
            currency: "SDG",
            decimal: 2,
            headers: [
                { text: this.$t('code'),  value: "code" },
                { text: this.$t('date'), value: "date" },
                { text: this.$t('validity_date'), value: "validity_date" },
                { text: this.$t('customer'), sortable: false, value: "customer" },
                { text: this.$t('delivered'), sortable: false, value: "delivered" },
                { text: this.$t("extended"), sortable: false, value: "extended" },
                { text: this.$t('remaining'), sortable: false, value: "remaining" },
                { text: this.$t('total'), sortable: false, value: "total" },
                { text: this.$t('state'), sortable: false, value: "state" }
            ],
            selected: [],
            options: {},
            contextMenu: {
                display: false,
                position: {
                    x: 0,
                    y: 0,
                }
            }
        }
    },
    computed: {
        ...mapState('authentication', ["authorizations"]),
        contextMenuItems() {
            return [
                { title: this.$t("show_sales_order"), icon: "mdi-eye", action: "openSalesOrderDisplayDialog", disabled: !this.authorizations.customers.read},
                { title: this.$t("edit_sales_order"), icon: "mdi-pencil", action: "openSalesOrderEditDialog", disabled: !this.isEditable},
                { title: this.$t("delete_sales_order"), icon: "mdi-delete", action: "openSalesOrderDeleteDialog", disabled: !this.isDeletable}
            ]
        },
        isDeletable() {
            if(!this.getSelected.meta) return false;
            else return this.getSelected.meta.deletable && this.authorizations.sales_orders.delete
        },
        isEditable() {
            if(!this.getSelected.meta) return false;
            else return this.getSelected.meta.editable && this.authorizations.sales_orders.update
        },
        getSelected() {
            return this.selected.length == 1 ? this.selected[0] : {};
        }
    },
    methods: {
        formatMoney, formatNumber,
        ...mapActions("sales/salesOrder", ["removeSalesOrder"]),
        getColor(date) {
            var today = moment().format("YYYY-MM-DD");
            if (date >= today) return 'green'
            else if (date > today) return 'orange'
            else return 'red'
        },
        clearSelected() {
            this.selected = [];
            this.$emit("selected:clear");
        },
        setSelectedFromCheckbox(payload) {
            if(payload.value) this.setSelected(payload.item);
            else this.clearSelected();
        },
        setSelected(salesOrder) {
            this.clearSelected();
            this.selected.push(salesOrder);
            this.$emit("selected:row", salesOrder);
        },
        openContextMenu(e, payload) {
            e.preventDefault();
            this.contextMenu.position.x = e.clientX
            this.contextMenu.position.y = e.clientY
            this.contextMenu.display = false;
            this.$nextTick(() => {
                this.contextMenu.display = true;
            });
            this.setSelected(payload.item);
        },
        contextMenuClicked(action) {
            this[action]();
        },
        openSalesOrderDisplayDialog() {
            this.$refs.salesOrderDisplayDialog.open({id: this.getSelected.id});
        },
        openSalesOrderEditDialog() {
            this.$refs.salesOrderDialog.open({id: this.getSelected.id, editMode: true})
        },
        openSalesOrderDeleteDialog() {
            this.$refs.deleteSingleDialog.open({target: 'salesOrder', id: this.getSelected.id});
        },
        onDeleteDialogSubmit(params) {
            if(params.target == 'salesOrder') {
                this.removeSalesOrder(params)
                    .then(() => {
                        this.$refs.deleteSingleDialog.close();
                        this.$emit("updated", this.options);
                        this.$refs.alertDialog.open({type: "success", message: this.$t("records_delete_success")});
                    })
                    .catch((error) => {
                        this.$refs.deleteSingleDialog.close();
                        let message = error.response ? error.response.data.message : error;
                        this.$refs.alertDialog.open({type: "error", message});
                    })
            }
        },
    }
}
</script>